import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { UspBar as UspBarProps } from 'corgi-types';
import { display } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { Grid, GridItem } from 'common/components/Grid';
import { useLocalization } from 'common/providers/LocaleProvider';
import { textWithEllipsis } from 'common/theme/styles';
import { TrackingEventName, TrackingPlacement } from '../types';
import Link from './common/Link';
import Text from './common/Text';
import Entrypoint from './Entrypoint';
import UrgentMessageUspBar from './UrgentMessageUspBar';

const FIRST_ENTRIES_DISPLAY = ['none', 'none', 'none', 'flex'];
const LAST_ENTRIES_DISPLAY = ['none', 'none', 'flex', 'flex'];

const mainUspBarStyles: ComponentProps<typeof GridItem> = {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
};

const MainText = styled(Text)`
  ${textWithEllipsis(1)};
`;

const MainLink = styled(Link)`
  ${textWithEllipsis(1)};
`;

const UnstyledEntryPointWrapper = styled(Flex)<any>`
  ${display};
`;

const EntryPointWrapper = (props: ComponentProps<typeof UnstyledEntryPointWrapper>) => (
  <UnstyledEntryPointWrapper
    display={['none', null, 'flex']}
    justifyContent="flex-end"
    {...props}
  />
);

const UspBar = ({ text, textUrl, entryPoints, urgentMessage }: UspBarProps) => {
  const { t } = useLocalization();
  const { fullUrl, target } = textUrl || {};
  const firstEntries = entryPoints.slice(0, -3);
  const lastEntries = entryPoints.slice(-3);

  return (
    <>
      <Grid as="nav" bg="sys.neutral.background.strong" aria-label={t('h24_service_menu_label')}>
        <GridItem {...mainUspBarStyles}>
          {fullUrl ? (
            <MainLink
              href={fullUrl}
              target={target || undefined}
              tracking={{
                eventName: TrackingEventName.Click,
                clickedText: text,
                placement: TrackingPlacement.UspBar,
              }}
            >
              {text}
            </MainLink>
          ) : (
            <MainText>{text}</MainText>
          )}
          <EntryPointWrapper>
            {firstEntries.map((props, index) => (
              <Entrypoint key={`first-${index}`} display={FIRST_ENTRIES_DISPLAY} {...props} />
            ))}
            {lastEntries.map((props, index) => (
              <Entrypoint key={`last-${index}`} display={LAST_ENTRIES_DISPLAY} {...props} />
            ))}
          </EntryPointWrapper>
        </GridItem>
      </Grid>

      {Boolean(urgentMessage.text) && <UrgentMessageUspBar {...urgentMessage} />}
    </>
  );
};

export default UspBar;
